import { useCallback, useEffect, useState } from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import DialogActions from '@mui/joy/DialogActions'
import DialogContent from '@mui/joy/DialogContent'
import DialogTitle from '@mui/joy/DialogTitle'
import Divider from '@mui/joy/Divider'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Switch from '@mui/joy/Switch'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import TabPanel from '@mui/joy/TabPanel'
import Tabs from '@mui/joy/Tabs'
import Table from '@mui/joy/Table'
import Textarea from '@mui/joy/Textarea'
import Typography from '@mui/joy/Typography'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import {
    pushNotificationsApi,
    type HistoryItem,
    type InactivityThreshold,
    type PushAppType,
    type PushTargetType,
    type RetentionConfig,
} from 'api/pushNotifications'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const APP_TYPE_LABEL: Record<PushAppType, string> = {
    dispatcher: 'Dispatcher',
    driver: 'Driver',
    both: 'All apps',
}

// ─── Send Form ────────────────────────────────────────────────────────────────

interface Draft {
    targetType: PushTargetType
    appType: PushAppType
    projectName: string
    title: string
    body: string
}

const emptyDraft = (): Draft => ({
    targetType: 'all',
    appType: 'dispatcher',
    projectName: '',
    title: '',
    body: '',
})

interface PreviewState {
    loading: boolean
    deviceCount: number | null
    userCount: number | null
}

interface SendTabProps {
    onSendSuccess: () => void
}

function SendTab({ onSendSuccess }: SendTabProps) {
    const [draft, setDraft] = useState<Draft>(emptyDraft())
    const [preview, setPreview] = useState<PreviewState>({ loading: false, deviceCount: null, userCount: null })
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [sending, setSending] = useState(false)
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; color: 'success' | 'danger' }>({
        open: false,
        message: '',
        color: 'success',
    })

    const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
        setDraft((prev) => ({ ...prev, [key]: value }))
        // Reset preview whenever target changes
        if (key === 'targetType' || key === 'appType' || key === 'projectName') {
            setPreview({ loading: false, deviceCount: null, userCount: null })
        }
    }

    const canPreview = draft.title.trim() !== '' && draft.body.trim() !== '' && (draft.targetType === 'all' || draft.projectName.trim() !== '')

    const canSend = canPreview && preview.deviceCount !== null && preview.deviceCount > 0

    const handlePreview = async () => {
        setPreview({ loading: true, deviceCount: null, userCount: null })
        try {
            const result = await pushNotificationsApi.preview({
                type: draft.targetType,
                appType: draft.appType,
                ...(draft.targetType === 'project' ? { projectName: draft.projectName.trim() } : {}),
            })
            setPreview({ loading: false, deviceCount: result.deviceCount, userCount: result.userCount })
        } catch {
            setPreview({ loading: false, deviceCount: null, userCount: null })
            setSnackbar({ open: true, message: 'Failed to fetch device count', color: 'danger' })
        }
    }

    const executeSend = async () => {
        setConfirmOpen(false)
        setSending(true)
        try {
            const result = await pushNotificationsApi.send(
                {
                    type: draft.targetType,
                    appType: draft.appType,
                    ...(draft.targetType === 'project' ? { projectName: draft.projectName.trim() } : {}),
                },
                { title: draft.title.trim(), body: draft.body.trim() }
            )
            setSnackbar({ open: true, message: `Sent to ${result.delivered}/${result.total} devices`, color: 'success' })
            setDraft(emptyDraft())
            setPreview({ loading: false, deviceCount: null, userCount: null })
            onSendSuccess()
        } catch {
            setSnackbar({ open: true, message: 'Send failed — check logs', color: 'danger' })
        } finally {
            setSending(false)
        }
    }

    const scopeLabel =
        draft.targetType === 'all'
            ? `all ${APP_TYPE_LABEL[draft.appType].toLowerCase()} users`
            : `project "${draft.projectName}" (${APP_TYPE_LABEL[draft.appType]})`

    return (
        <>
            <Stack spacing={3} sx={{ maxWidth: 600 }}>
                <Typography level="title-md">Compose Notification</Typography>

                {/* Target */}
                <Card variant="outlined">
                    <Typography level="title-sm" sx={{ mb: 2 }}>
                        Target
                    </Typography>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={2}>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>Scope</FormLabel>
                                <Select value={draft.targetType} onChange={(_, v) => v && set('targetType', v as PushTargetType)}>
                                    <Option value="all">All users</Option>
                                    <Option value="project">By project</Option>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <FormLabel>App type</FormLabel>
                                <Select value={draft.appType} onChange={(_, v) => v && set('appType', v as PushAppType)}>
                                    <Option value="dispatcher">Dispatcher</Option>
                                    <Option value="driver">Driver</Option>
                                    <Option value="both">Both</Option>
                                </Select>
                            </FormControl>
                        </Stack>
                        {draft.targetType === 'project' && (
                            <FormControl>
                                <FormLabel>Project name</FormLabel>
                                <Input placeholder="e.g. acme-logistics" value={draft.projectName} onChange={(e) => set('projectName', e.target.value)} />
                            </FormControl>
                        )}
                    </Stack>
                </Card>

                {/* Compose */}
                <Card variant="outlined">
                    <Typography level="title-sm" sx={{ mb: 2 }}>
                        Message
                    </Typography>
                    <Stack spacing={2}>
                        <FormControl>
                            <FormLabel>Title</FormLabel>
                            <Input placeholder="Notification title" value={draft.title} onChange={(e) => set('title', e.target.value)} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Body</FormLabel>
                            <Textarea placeholder="Notification body text" minRows={3} value={draft.body} onChange={(e) => set('body', e.target.value)} />
                        </FormControl>
                    </Stack>
                </Card>

                {/* Preview + Send */}
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="outlined" color="neutral" disabled={!canPreview || preview.loading} onClick={handlePreview}>
                        {preview.loading ? <CircularProgress size="sm" /> : 'Preview'}
                    </Button>

                    {preview.deviceCount !== null && (
                        <Chip color={preview.deviceCount > 0 ? 'primary' : 'warning'} variant="soft">
                            {preview.deviceCount} device{preview.deviceCount !== 1 ? 's' : ''} across {preview.userCount} user
                            {preview.userCount !== 1 ? 's' : ''}
                        </Chip>
                    )}

                    <Button color="danger" disabled={!canSend || sending} loading={sending} onClick={() => setConfirmOpen(true)} sx={{ ml: 'auto' }}>
                        Send now
                    </Button>
                </Stack>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    color={snackbar.color}
                    variant="soft"
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                >
                    {snackbar.message}
                </Snackbar>
            </Stack>

            {/* Confirmation dialog */}
            <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog">
                    <DialogTitle>
                        <WarningRoundedIcon />
                        Confirm send
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            This will send <strong>"{draft.title}"</strong> to {preview.deviceCount} device{preview.deviceCount !== 1 ? 's' : ''} ({scopeLabel}
                            ). This cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="solid" color="danger" onClick={executeSend}>
                            Send
                        </Button>
                        <Button variant="plain" color="neutral" onClick={() => setConfirmOpen(false)}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </>
    )
}

// ─── History Tab ──────────────────────────────────────────────────────────────

function formatTarget(item: HistoryItem): string {
    const appLabel = APP_TYPE_LABEL[item.target.appType]
    if (item.target.type === 'project') return `${item.target.projectName} / ${appLabel}`
    return `All users / ${appLabel}`
}

interface HistoryTabProps {
    refreshKey: number
}

function HistoryTab({ refreshKey }: HistoryTabProps) {
    const [items, setItems] = useState<HistoryItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const load = useCallback(() => {
        setLoading(true)
        setError(null)
        pushNotificationsApi
            .getHistory()
            .then(setItems)
            .catch(() => setError('Failed to load history'))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        load()
    }, [load, refreshKey])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        )
    }

    if (error) {
        return <Typography color="danger">{error}</Typography>
    }

    if (items.length === 0) {
        return (
            <Typography level="body-sm" textColor="text.tertiary">
                No notifications sent yet.
            </Typography>
        )
    }

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table stickyHeader hoverRow>
                <thead>
                    <tr>
                        <th style={{ width: 160 }}>Sent at</th>
                        <th style={{ width: 160 }}>Sent by</th>
                        <th>Target</th>
                        <th>Title</th>
                        <th style={{ width: 130 }}>Delivered</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => (
                        <tr key={item.id}>
                            <td>
                                <Typography level="body-xs">{new Date(item.sentAt).toLocaleString()}</Typography>
                            </td>
                            <td>
                                <Typography level="body-xs">{item.sentBy}</Typography>
                            </td>
                            <td>
                                <Typography level="body-xs">{formatTarget(item)}</Typography>
                            </td>
                            <td>
                                <Typography level="body-xs" noWrap>
                                    {item.payload.title}
                                </Typography>
                            </td>
                            <td>
                                <Chip size="sm" color={item.stats.failed === 0 ? 'success' : 'warning'} variant="soft">
                                    {item.stats.delivered}/{item.stats.total}
                                </Chip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Sheet>
    )
}

// ─── Retention Tab ────────────────────────────────────────────────────────────

const INACTIVITY_LABELS: Record<InactivityThreshold, string> = {
    '1': 'Inactive 1–3 days',
    '3': 'Inactive 3–7 days',
    '7': 'Inactive 7–30 days',
    '30': 'Inactive 30–60 days',
}

const INACTIVITY_ORDER: InactivityThreshold[] = ['1', '3', '7', '30']

function RetentionTab() {
    const [config, setConfig] = useState<RetentionConfig | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [sendingNow, setSendingNow] = useState(false)
    const [confirmSendOpen, setConfirmSendOpen] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; color: 'success' | 'danger' }>({
        open: false,
        message: '',
        color: 'success',
    })

    useEffect(() => {
        setLoading(true)
        pushNotificationsApi
            .getRetentionConfig()
            .then(setConfig)
            .catch(() => setError('Failed to load retention config'))
            .finally(() => setLoading(false))
    }, [])

    const formatLastSent = (key: string): string => {
        const iso = config?.lastSent?.[key]
        return iso ? new Date(iso).toLocaleString() : 'Never'
    }

    const setInactivityTier = (threshold: InactivityThreshold, field: 'title' | 'body' | 'enabled', value: string | boolean) => {
        setConfig((prev) =>
            prev
                ? {
                      ...prev,
                      inactivity: {
                          ...prev.inactivity,
                          tiers: {
                              ...prev.inactivity.tiers,
                              [threshold]: { ...prev.inactivity.tiers[threshold], [field]: value },
                          },
                      },
                  }
                : prev
        )
    }

    const isTemplateValid = (t: { title: string; body: string }) => t.title.trim() !== '' && t.body.trim() !== ''

    const canSave = config !== null && INACTIVITY_ORDER.every((t) => isTemplateValid(config.inactivity.tiers[t])) && isTemplateValid(config.limitReset.template)

    const handleSave = async () => {
        if (!config) return
        setSaving(true)
        try {
            const saved = await pushNotificationsApi.updateRetentionConfig(config)
            setConfig(saved)
            setSnackbar({ open: true, message: 'Retention settings saved', color: 'success' })
        } catch {
            setSnackbar({ open: true, message: 'Failed to save — check that every message has a title and body', color: 'danger' })
        } finally {
            setSaving(false)
        }
    }

    const handleSendNow = async () => {
        setConfirmSendOpen(false)
        setSendingNow(true)
        try {
            const result = await pushNotificationsApi.runInactivityNow()
            if (!result.enabled) {
                setSnackbar({ open: true, message: 'Inactivity campaign is off — enable it first', color: 'danger' })
                return
            }
            const delivered = result.tiers.reduce((sum, t) => sum + t.delivered, 0)
            const failed = result.tiers.reduce((sum, t) => sum + t.failed, 0)
            setSnackbar({
                open: true,
                message: `Sent — ${delivered} delivered${failed > 0 ? `, ${failed} failed (dead/stale tokens)` : ''}`,
                color: failed > 0 && delivered === 0 ? 'danger' : 'success',
            })
            // Refresh lastSent timestamps
            pushNotificationsApi
                .getRetentionConfig()
                .then(setConfig)
                .catch(() => undefined)
        } catch {
            setSnackbar({ open: true, message: 'Send failed — check logs', color: 'danger' })
        } finally {
            setSendingNow(false)
        }
    }

    const masterOff = config !== null && !config.inactivity.enabled

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        )
    }

    if (error || !config) {
        return <Typography color="danger">{error ?? 'Failed to load retention config'}</Typography>
    }

    return (
        <Stack spacing={3} sx={{ maxWidth: 640 }}>
            {/* Inactivity win-back */}
            <Card variant="outlined">
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Box>
                        <Typography level="title-sm">Inactivity win-back</Typography>
                        <Typography level="body-xs" textColor="text.tertiary">
                            Automatically nudge dispatchers who haven't signed in for 1, 3, 7, or 30 days.
                        </Typography>
                    </Box>
                    <Switch
                        checked={config.inactivity.enabled}
                        onChange={(e) => setConfig({ ...config, inactivity: { ...config.inactivity, enabled: e.target.checked } })}
                    />
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={2}>
                    {INACTIVITY_ORDER.map((threshold) => {
                        const tier = config.inactivity.tiers[threshold]
                        const tierDisabled = !config.inactivity.enabled || !tier.enabled
                        return (
                            <Box key={threshold}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                    <Box>
                                        <Typography level="title-sm">{INACTIVITY_LABELS[threshold]}</Typography>
                                        <Typography level="body-xs" textColor="text.tertiary">
                                            Last sent: {formatLastSent(`inactivity_${threshold}`)}
                                        </Typography>
                                    </Box>
                                    <Switch
                                        size="sm"
                                        checked={tier.enabled}
                                        disabled={!config.inactivity.enabled}
                                        onChange={(e) => setInactivityTier(threshold, 'enabled', e.target.checked)}
                                    />
                                </Stack>
                                <Stack spacing={1}>
                                    <FormControl>
                                        <FormLabel>Title</FormLabel>
                                        <Input
                                            value={tier.title}
                                            onChange={(e) => setInactivityTier(threshold, 'title', e.target.value)}
                                            disabled={tierDisabled}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Body</FormLabel>
                                        <Textarea
                                            minRows={2}
                                            value={tier.body}
                                            onChange={(e) => setInactivityTier(threshold, 'body', e.target.value)}
                                            disabled={tierDisabled}
                                        />
                                    </FormControl>
                                </Stack>
                            </Box>
                        )
                    })}
                </Stack>
            </Card>

            {/* Limit-reset nudge */}
            <Card variant="outlined">
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Box>
                        <Typography level="title-sm">Daily limit-reset nudge</Typography>
                        <Typography level="body-xs" textColor="text.tertiary">
                            Remind FREE-plan dispatchers their searches refreshed. Sends at 8am their local time, or 6am SF time when unknown.
                        </Typography>
                        <Typography level="body-xs" textColor="text.tertiary">
                            Last sent: {formatLastSent('limit_reset')}
                        </Typography>
                    </Box>
                    <Switch
                        checked={config.limitReset.enabled}
                        onChange={(e) => setConfig({ ...config, limitReset: { ...config.limitReset, enabled: e.target.checked } })}
                    />
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={1}>
                    <FormControl>
                        <FormLabel>Title</FormLabel>
                        <Input
                            value={config.limitReset.template.title}
                            onChange={(e) =>
                                setConfig({
                                    ...config,
                                    limitReset: { ...config.limitReset, template: { ...config.limitReset.template, title: e.target.value } },
                                })
                            }
                            disabled={!config.limitReset.enabled}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>Body</FormLabel>
                        <Textarea
                            minRows={2}
                            value={config.limitReset.template.body}
                            onChange={(e) =>
                                setConfig({
                                    ...config,
                                    limitReset: { ...config.limitReset, template: { ...config.limitReset.template, body: e.target.value } },
                                })
                            }
                            disabled={!config.limitReset.enabled}
                        />
                    </FormControl>
                </Stack>
            </Card>

            <Stack direction="row" spacing={2} alignItems="center">
                <Button color="primary" loading={saving} disabled={!canSave || saving} onClick={handleSave}>
                    Save settings
                </Button>
                <Button variant="outlined" color="danger" loading={sendingNow} disabled={sendingNow || masterOff} onClick={() => setConfirmSendOpen(true)}>
                    Send inactivity push now
                </Button>
                <Typography level="body-xs" textColor="text.tertiary">
                    Sends all enabled tiers immediately (no scheduler). Save edits first.
                </Typography>
            </Stack>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                color={snackbar.color}
                variant="soft"
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                {snackbar.message}
            </Snackbar>

            {/* Confirm immediate send */}
            <Modal open={confirmSendOpen} onClose={() => setConfirmSendOpen(false)}>
                <ModalDialog variant="outlined" role="alertdialog">
                    <DialogTitle>
                        <WarningRoundedIcon />
                        Send inactivity push now
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            This sends every <strong>enabled</strong> inactivity tier to its matching dispatchers right now. There is no dedup — running this
                            twice sends twice. Check each tier's "Last sent" before confirming. Continue?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="solid" color="danger" onClick={handleSendNow}>
                            Send now
                        </Button>
                        <Button variant="plain" color="neutral" onClick={() => setConfirmSendOpen(false)}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Stack>
    )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PushNotificationsPage() {
    const [historyRefreshKey, setHistoryRefreshKey] = useState(0)

    return (
        <Box sx={{ p: 3 }}>
            <Stack spacing={1} sx={{ mb: 3 }}>
                <Typography level="h3">Push Notifications</Typography>
                <Typography level="body-sm" textColor="text.tertiary">
                    Send push notifications to dispatcher or driver app users.
                </Typography>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <Tabs defaultValue="send">
                <TabList>
                    <Tab value="send">Send</Tab>
                    <Tab value="retention">Retention</Tab>
                    <Tab value="history">History</Tab>
                </TabList>
                <TabPanel value="send" sx={{ pt: 3 }}>
                    <SendTab onSendSuccess={() => setHistoryRefreshKey((k) => k + 1)} />
                </TabPanel>
                <TabPanel value="retention" sx={{ pt: 3 }}>
                    <RetentionTab />
                </TabPanel>
                <TabPanel value="history" sx={{ pt: 3 }}>
                    <HistoryTab refreshKey={historyRefreshKey} />
                </TabPanel>
            </Tabs>
        </Box>
    )
}
